import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { createObject as createObjectUnsafe } from "tweenly"
import { ObjectsStateT } from "../store/slices/objects.slice"
import { transformsUnderParent } from "./affineResize.helpers"
import { sumArray } from "./array.helpers"

/**
 * `createObject` from tweenly internally calls `filterAndSortSiblings`, which sorts the
 * passed array IN PLACE. Callers usually hand it the result of the memoized `selectObjects`,
 * which would reorder that shared array for every other consumer. Always pass a copy.
 */
export const createObject = (
    objects: AnySceneObjectT[],
    initialObject: AnySceneObjectT,
    parentId: string | null,
    id?: string,
    isGroup?: boolean
): AnySceneObjectT => createObjectUnsafe([...objects], initialObject, parentId, id, isGroup)

/**
 * A NEW object to add under `parentId` (null: the root), drawn as it would be at the root: its
 * `matrix` makes up for whatever the parents turn or skew (see transformsUnderParent), so a
 * child added to a turned parent is not born turned with it.
 *
 * Only for objects the user adds — not for a group, a duplicate or the insides of a ticker,
 * which are built in their parent's space on purpose.
 */
export const createChildObject = (
    objects: AnySceneObjectT[],
    initialObject: AnySceneObjectT,
    parentId: string | null,
    id?: string
): AnySceneObjectT => {
    const object = createObject(objects, initialObject, parentId, id)
    if (!parentId) return object
    const objectsById = Object.fromEntries(objects.map((each) => [each.id, each]))
    const transforms = transformsUnderParent(object.transforms, null, parentId, objectsById)
    return transforms === object.transforms ? object : { ...object, transforms }
}

/**
 * Objects whose dynamic text may be refreshed by the canvas timer.
 *
 * In the editor the children of a ticker are excluded — they have their own text machinery
 * (`tickerTextRequester` / the `*Ticker` classes) which the periodic `setDynamicTexts` would
 * otherwise overwrite. The preview still relies on the plain 0.5 s refresh for everything.
 *
 * The ticker ids are collected into a Set first: the previous version ran
 * `objects.find(...)` inside `objects.filter(...)`, i.e. O(n²) — 40 000 comparisons per call
 * on a 200 object graphic.
 */
export const objectsForDynamicTexts = (
    objects: AnySceneObjectT[],
    isEditor: boolean
): AnySceneObjectT[] => {
    if (!isEditor) return objects

    const tickerIds = new Set(
        objects.filter((object) => object.type === "ticker").map((object) => object.id)
    )

    return objects.filter((object) => !(object.parentId && tickerIds.has(object.parentId)))
}

export type ObjectStyles = {
    left: number
    top: number
    width: number
    height: number
}

export const getWidth = (object: AnySceneObjectT) => {
    return object.styles.find((style) => style.property === "width")
}

export const getHeight = (object: AnySceneObjectT) => {
    return object.styles.find((style) => style.property === "height")
}

export const getResultMultiAlignment = (
    object: AnySceneObjectT,
    value: string,
    styles: ObjectStyles
) => {
    switch (value) {
        case "horizontalLeft":
            return styles.left
        case "horizontalRight":
            return styles.left + styles.width - Number(getWidth(object)?.value)
        case "horizontalCenter":
            return styles.width / 2 + styles.left - Number(getWidth(object)?.value) / 2
        case "verticalTop":
            return styles.top
        case "verticalCenter":
            return styles.height / 2 + styles.top - Number(getHeight(object)?.value) / 2
        case "verticalBottom":
            return styles.top + styles.height - Number(getHeight(object)?.value)
        default:
            return 0
    }
}

/**
 * Returns object styles (left, top, with, height) from object
 * @param object AnySceneObjectT
 */
export const getObjectPositioning = (object: AnySceneObjectT) => {
    return {
        position: object.styles.find((style) => style.property === "position")?.value,
        left: object.styles.find((style) => style.property === "left")?.value,
        top: object.styles.find((style) => style.property === "top")?.value,
        width: object.styles.find((style) => style.property === "width")?.value,
        height: object.styles.find((style) => style.property === "height")?.value,
    }
}

/**
 * Check if an object or any of its descendants uses position: static (flex children).
 * Used to disable grouping/wrapping which requires absolute positioning.
 */
export const hasStaticDescendant = (
    object: AnySceneObjectT,
    allObjects: Record<string, AnySceneObjectT>
): boolean => {
    const pos = object.styles.find((s) => s.property === "position")
    if (pos?.value === "static") return true
    return object.childIds.some((id) => {
        const child = allObjects[id]
        return child ? hasStaticDescendant(child, allObjects) : false
    })
}

/**
 * Returns gab between object in selection,
 * @param wrapperObjectValue number,  height or width of wrapper object
 * @param values number[], values is array of heights or widths
 * @param gapNumbers number, numbers of gap between objects
 */
export const getDistributionGap = (
    wrapperObjectValue: number,
    values: number[],
    gapNumbers: number
): number => {
    const totalValues = values.reduce((a, b) => a + b, 0) // Summing the array
    const difference = Number(wrapperObjectValue) - Number(totalValues) // Subtracting the sum from the wrapperObjectValue
    const gap = difference / gapNumbers // Dividing the difference by gapNumbers
    return Math.round(gap)
}

/**
 * Returns desc array of objects sorted by object style
 * @param array AnySceneObjectT[]
 * @param sortedBy 'left' | 'top' | 'width' | 'height'
 */
export const getSortedObjectArray = (
    array: AnySceneObjectT[],
    sortedBy: "left" | "top" | "width" | "height"
) => {
    return array.sort((a, b) =>
        getObjectPositioning(a)[sortedBy] > getObjectPositioning(b)[sortedBy]
            ? 1
            : getObjectPositioning(b)[sortedBy] > getObjectPositioning(a)[sortedBy]
            ? -1
            : 0
    )
}

export const updateObjectStyle = (
    object: AnySceneObjectT,
    property: string | number,
    value: any
): SimpleStyleT[] | undefined => {
    const style: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === property
    )
    if (!style) return
    if (style.value === value && style.property === property) {
        return
    } else {
        const restStyles: SimpleStyleT[] = object.styles.filter(
            (style) => style.property !== property
        )
        const updatedStyles: SimpleStyleT[] = [
            ...restStyles,
            {
                ...style,
                value: value,
            },
        ]
        return updatedStyles
    }
}

export const getAbsoluteTopLeftPosition = (
    objects: ObjectsStateT,
    object: AnySceneObjectT,
    top: number[] = [],
    left: number[] = []
) => {
    if (object) {
        const actualStyles = getObjectPositioning(object)
        top.push(Number(actualStyles.top))
        left.push(Number(actualStyles.left))
        if (object.parentId) {
            const parentObject = objects.value[object.parentId]
            getAbsoluteTopLeftPosition(objects, parentObject, top, left)
        }
    }
    return {
        top: sumArray(top),
        left: sumArray(left),
    }
}
